import type { SupabaseClient, User } from '@supabase/supabase-js';

/**
 * Retry wrapper for `supabase.auth.getUser()`.
 *
 * Security: callers still validate through `auth.getUser()`, not
 * `getSession()`. The retry only gives tolerance for transient Auth REST
 * failures and still fails closed when every attempt fails.
 */
export async function getAuthUserWithRetry(
  supabase: SupabaseClient,
  opts: { retries?: number; delayMs?: number } = {},
): Promise<{ data: { user: User | null }; error: Error | null }> {
  const retries = opts.retries ?? 1;
  const delayMs = opts.delayMs ?? 100;
  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await supabase.auth.getUser();
      if (result.data?.user) {
        return { data: { user: result.data.user }, error: null };
      }
      // user is null. Do NOT short-circuit on `!result.error`: under Auth-REST /
      // pooler saturation `getUser()` returns the anomalous `{ user: null, error: null }`
      // ("soft null") rather than throwing — and the original early-return treated that
      // as a definitive logout, bouncing valid sessions to /login mid-work (the residual
      // random-logout after the 2026-05-27 single-retry fix). A genuinely signed-out
      // session returns an AuthSessionMissingError, so this retry does not add latency to
      // real logouts beyond what the error path already incurred. Capture any error and
      // retry; only resolve to null once all attempts are exhausted.
      if (result.error) {
        lastError = result.error as Error;
      }
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  // Still fail closed. If every attempt was a soft-null (no thrown error), surface
  // a synthetic error so the caller's warning log can distinguish "Auth REST kept
  // returning null" from a genuine signed-out session (which carries a real error).
  return {
    data: { user: null },
    error:
      lastError ??
      new Error(
        'getAuthUserWithRetry: getUser() returned a null user with no error on all attempts',
      ),
  };
}

/** Only explicit no-session/invalid-grant responses establish a signed-out principal. */
export function isConfirmedAuthSignOut(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const value = error as { name?: string; code?: string; status?: number; message?: string };
  // Network, timeout and gateway failures always remain indeterminate.
  if (
    value.status === 429 ||
    (value.status !== undefined && value.status >= 500) ||
    /network|fetch failed|timeout|timed out/i.test(value.message ?? '')
  )
    return false;
  return (
    value.name === 'AuthSessionMissingError' ||
    [
      'session_not_found',
      'session_expired',
      'refresh_token_not_found',
      'refresh_token_already_used',
      'user_not_found',
    ].includes(value.code ?? '')
  );
}
